import {Component, Inject, Input, OnDestroy, OnInit, Optional} from "@angular/core";
import { Location, NgClass, NgOptimizedImage, UpperCasePipe } from "@angular/common";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {BeforeLoginService} from "../../services/before-login.service";
import {SessionContext} from "../../models/session-context.model";
import {AuthService} from "../../services/auth.service";
import {DeviceDetectService} from "../../services/device-detect.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Subject} from "rxjs";
import {NgxHealthPlanService} from "../../features/health-plan/ngx-health-plan.service";
import {takeUntil} from "rxjs/operators";
import {environment} from "../../../environments/environment";
import {appearSlowlyAnimation} from "../../features/cookie-modal/cookie-modal-animation";
import {LinkHandlerDirective} from "../../directives/link-handler.directive";
import {NtConsentStatuses} from "../../models/nt-consent-statuses.model";
import {ConsentService} from "../../services/consent.service";


@Component({
  selector: "app-give-consent-modal",
  templateUrl: "./give-consent-modal.component.html",
  styleUrls: ["./give-consent-modal.component.css"],
  animations: [appearSlowlyAnimation],
  imports: [
    NgClass,
    UpperCasePipe,
    TranslateModule,
    NgOptimizedImage,
    LinkHandlerDirective
]
})
export class GiveConsentModalComponent implements OnInit, OnDestroy {
  @Input() message: { isSuccess?: boolean, title?: string; description?: string };

  showModal: boolean;
  expandText: boolean = false;
  isMobile: boolean;
  hasHealthPlan: boolean;
  ntInsuranceCheck: boolean;
  ntPrescriptionCheck: boolean;
  sessionContext: SessionContext = JSON.parse(
    sessionStorage.getItem("sessionContext"),
  );
  ntConsentStatus: NtConsentStatuses;
  currentLanguage: string;
  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<GiveConsentModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data,
    private authService: AuthService,
    private deviceDetectService: DeviceDetectService,
    private beforeLoginService: BeforeLoginService,
    private consentService: ConsentService,
    private healthPlanService: NgxHealthPlanService,
    private location: Location,
    private translate: TranslateService,
  ) {
    this.currentLanguage = this.translate.currentLang;
  }

  ngOnInit(): void {
    this.isMobile = this.deviceDetectService.isMobile;
    this.healthPlanService.healthPlanChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(healthPlanChanges => {
          this.hasHealthPlan = this.determineHasHealthPlan(healthPlanChanges);
          this.initializeConsentContext();
        },
      );
  }

  determineHasHealthPlan(healthPlanChanges: any): boolean {
    if (healthPlanChanges && healthPlanChanges.length > 0) {
      if (this.healthPlanService.activeHealthPlan) {
        return true;
      }
    }
    return false;
  }

  initializeConsentContext() {
    const consentString = sessionStorage.getItem("ConsentContext");
    const consentContext = JSON.parse(consentString);
    if (this.consentService.fetchedConsentRefs &&
      this.consentService.fetchedConsentRefs.validPurposeDeclarationIds === undefined) {
      this.ntInsuranceCheck = false;
      this.ntPrescriptionCheck = false;
    } else {
      this.ntInsuranceCheck = Boolean(consentContext.validPurposeDeclarationIds[environment.consentService.insuranceDeclarationId]);
      this.ntPrescriptionCheck = Boolean(consentContext.validPurposeDeclarationIds[environment.consentService.prescriptionDeclarationId]);
    }
    this.ntConsentStatus = this.getConsentStatus();
  }

  getConsentStatus(): NtConsentStatuses {
    const termsOutput = this.sessionContext.termsOutput;
    const prescriptionsAccepted = termsOutput.prescriptionsAccepted;
    const termsAccepted = termsOutput.termsAccepted;

    //USER TERMS ACCEPTED and PRESCRIPTION TERMS ACCEPTED. NT INSURANCE ACCEPTED but no NT PRESCRIPTION CONSENT
    if ((this.hasHealthPlan || !this.hasHealthPlan) && this.ntInsuranceCheck && !this.ntPrescriptionCheck && prescriptionsAccepted) {
      return NtConsentStatuses.ALL_TERMS_ACCEPTED_NT_PRESCRIPTION_CONSENT_REQUIRED;
    }
    //USER TERMS ACCEPTED no NT INSURANCE CONSENT (consent reference null or not valid).
    else if (!this.ntInsuranceCheck && termsAccepted && !prescriptionsAccepted) {
      return NtConsentStatuses.NT_INSURANCE_CONSENT_REQUIRED;
    }
    //USER TERMS ACCEPTED and PRESCRIPTION TERMS ACCEPTED no NT INSURANCE and PRESCRIPTION CONSENT (consent references null or not valid).
    else if (!this.ntInsuranceCheck && !this.ntPrescriptionCheck && termsAccepted && prescriptionsAccepted) {
      return NtConsentStatuses.NT_INSURANCE_AND_PRESCRIPTION_CONSENTS_REQUIRED;
    }
    //USER TERMS ACCEPTED and PRESCRIPTION TERMS ACCEPTED no NT INSURANCE CONSENT (consent references null or not valid). NT PRESCRIPTION CONSENT accepted .
    else if (!this.ntInsuranceCheck && this.ntPrescriptionCheck) {
      return NtConsentStatuses.ALL_TERMS_ACCEPTED_NT_INSURANCE_CONSENT_REQUIRED;
    }
    return NtConsentStatuses.NOT_DEFINED;
  }

  onGiveConsents() {
    const consentString = sessionStorage.getItem("ConsentContext");
    const consentContext = JSON.parse(consentString);
    window.location.href = consentContext.returnedUrl.url;
  }

  onLogout() {
    this.authService.clearSession();
  }

  onClose() {
    this.location.back();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly NtConsentStates = NtConsentStatuses;
}
